import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export const useTitle = (t) => {
  useEffect(() => {
    document.title = t;
  }, [t]);
};

export function Reveal({ children, delay = 0, y = 30, className, once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.85, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskLines({ lines, className, lineClassName, delay = 0, onLoad = false }) {
  return (
    <span className={className}>
      {lines.map((l, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClassName || ""}`}
            initial={{ y: "112%" }}
            {...(onLoad
              ? { animate: { y: "0%" } }
              : { whileInView: { y: "0%" }, viewport: { once: true, amount: 0.3 } })}
            transition={{ duration: 1, delay: delay + i * 0.13, ease: EASE }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function Chapter({ num, title, dark = false, center = false }) {
  return (
    <Reveal
      className={`flex items-center gap-4 ${center ? "justify-center" : ""} ${dark ? "text-gold-light" : "text-gold-text"}`}
    >
      <span className="font-serif italic text-xl leading-none">{num}</span>
      <span className="h-px w-12 bg-gold/60" />
      <span className="text-[11px] md:text-xs font-medium uppercase tracking-[0.28em]">{title}</span>
    </Reveal>
  );
}

export function Btn({ children, to, href, variant = "solid", testid, onClick, className = "", external = false }) {
  const base =
    "inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.22em] transition-all duration-500 hover:-translate-y-0.5";
  const styles = {
    solid: "bg-gold text-ink hover:bg-emerald hover:text-gold-light",
    emerald: "bg-emerald text-ivory hover:bg-gold hover:text-ink",
    outline: "border border-gold/70 text-current hover:bg-gold hover:text-ink",
    ghost: "border border-current/40 text-current hover:bg-current/10",
  }[variant];
  const cls = `${base} ${styles} ${className}`;
  if (href)
    return (
      <a data-testid={testid} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className={cls} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <Link data-testid={testid} to={to || "/enquire"} onClick={onClick} className={cls}>
      {children}
    </Link>
  );
}

export const inputCls =
  "w-full bg-white/80 border border-gold/30 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none px-4 py-3.5 text-sm text-ink placeholder:text-stone2/50 transition-all duration-300";

export function PageHero({ eyebrow, title, sub, img }) {
  return (
    <section className="relative flex min-h-[68vh] items-end overflow-hidden bg-emerald-deep pt-32 pb-16 md:min-h-[76vh] md:pb-24">
      <motion.img
        src={img}
        alt=""
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: EASE }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-emerald-deep via-emerald-deep/60 to-emerald-deep/30" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">
        <Chapter num="—" title={eyebrow} dark />
        <MaskLines
          onLoad
          delay={0.2}
          lines={title}
          lineClassName="font-serif text-ivory leading-[1.02] text-5xl md:text-7xl"
          className="mt-6 block"
        />
        {sub && <Reveal delay={0.5}><p className="mt-6 max-w-xl text-sm md:text-base text-ivory/70 leading-relaxed">{sub}</p></Reveal>}
      </div>
    </section>
  );
}

export function Marquee({ items }) {
  const row = (hidden) => (
    <div aria-hidden={hidden} className="flex shrink-0 items-center">
      {items.map((it, i) => (
        <span key={i} className="flex items-center">
          <span className="whitespace-nowrap px-6 font-serif text-2xl md:text-4xl italic text-emerald md:px-10">{it}</span>
          <span className="inline-block h-2 w-2 rotate-45 bg-gold/70" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="overflow-hidden border-y border-gold/25 bg-ivory py-5 md:py-6" data-testid="editorial-marquee">
      <div className="flex w-max animate-marquee">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}
